import Card from "components/card";
import Statistics from "./HUDStatistics";
import DollarIcon from "components/icons/WidgetIcon/DollarIcon";
import BedIcon from "components/icons/BedIcon";
import { MdDateRange } from "react-icons/md";
import { FaRegBuilding } from "react-icons/fa";
import { FaPercent } from "react-icons/fa6";
import { useDealData } from "contexts/DealContext";
import { formatCurrency, formatDate } from "utils/formatUtils";

const HudDebt = () => {
    const { currentFacility, currentDeal, currentGroup } = useDealData();

    // Check if the current facility is a specific (non-consolidated) individual facility
    const isSpecificFacilitySelected = currentFacility &&
        Object.entries(currentDeal?.facilities).find(
            ([name, facility]) => facility === currentFacility
        )?.[0]?.toLowerCase() !== 'consolidated';

    // Check if the current facility is a group (has isGroup property in details)
    const isGroupSelected = currentFacility?.details?.isGroup === true;

    // Show facility count and beds stats for consolidated view or group view
    const shouldShowFacilityStats = !isSpecificFacilitySelected || isGroupSelected;

    // Calculate the HUD facility count and total beds based on current view
    const getFacilityMetrics = () => {
        if (!currentDeal?.facilities) return { count: 0, totalBeds: 0 };

        let facilitiesWithHUD = [];

        // If a group is selected, only count facilities that are part of the group
        if (isGroupSelected && currentGroup && currentGroup.facilityIds) {
            facilitiesWithHUD = Object.entries(currentDeal.facilities)
                .filter(([facilityName, facility]) => {
                    // Skip consolidated facility
                    if (facilityName === "Consolidated") return false;

                    // Check if this facility is in the group
                    const isInGroup = currentGroup.facilityIds.includes(facility.details?.Facilities);

                    // Check if facility has HUD debt data
                    const hasHudDebt = facility.hudDebt?.Categories?.["HUD Debt"]?.length > 0;

                    return isInGroup && hasHudDebt;
                });
        }
        // For Consolidated view, count all facilities with HUD debt
        else if (!isSpecificFacilitySelected) {
            facilitiesWithHUD = Object.entries(currentDeal.facilities)
                .filter(([facilityName, facility]) => {
                    // Skip consolidated facility when counting
                    if (facilityName === "Consolidated") return false;

                    // Check if facility has HUD debt data
                    return facility.hudDebt?.Categories?.["HUD Debt"]?.length > 0;
                });
        }

        const totalBeds = facilitiesWithHUD.reduce((sum, [_, facility]) => {
            return sum + (parseInt(facility.details?.["Total Beds"]) || 0);
        }, 0);

        return {
            count: facilitiesWithHUD.length,
            totalBeds
        };
    };

    const { count: hudFacilityCount, totalBeds: hudTotalBeds } = getFacilityMetrics();

    // Get the HUD debt data from the current facility
    const hudDebtData = currentFacility?.hudDebt?.Categories?.["HUD Debt"] || [];

    const hasValue = (value) => {
        if (value === null || value === undefined) return false;
        if (value === "") return false;
        if (typeof value === "number" && value === 0) return false;
        if (typeof value === "string" && value.trim() === "") return false;
        return true;
    };

    // Get values directly from the array using find
    const endorsementDate = hudDebtData.find(item => item.Type === "Endorsement Date")?.Date;
    const interestRate = hudDebtData.find(item => item.Type === "Interest Rate")?.Rate;
    const originalLoan = hudDebtData.find(item => item.Type === "Original Loan")?.Amount;
    const currentLoanBalance = hudDebtData.find(item => item.Type === "Current Loan Balance")?.Amount;
    const debtService = hudDebtData.find(item => item.Type === "Debt Service")?.Amount;
    const term = hudDebtData.find(item => item.Type === "Term")?.Amount;

    // Check if any of our actual display values have data
    const hasHudData = [
        interestRate,
        originalLoan,
        currentLoanBalance,
        debtService,
        term,
        ...(shouldShowFacilityStats ? [] : [endorsementDate])
    ].some(value => hasValue(value));

    if (!hasHudData) {
        return (
            <Card extra={"items-center justify-around w-full p-[16px] bg-cover gap-2 flex flex-col"}>
                <h3 className="ml-6 mb-2 text-xl self-start font-bold text-gray-700 dark:text-white">HUD Debt</h3>
                <p>No data available</p>
            </Card>
        );
    }

    return (
        <Card extra={"items-center justify-around w-full p-[16px] bg-cover gap-2 flex flex-col"}>
            <h3 className="ml-6 mb-2 text-xl self-start font-bold text-gray-700 dark:text-white">HUD Debt</h3>
            <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-4">
                {shouldShowFacilityStats && (
                    <Statistics
                        title="HUD Facilities"
                        value={hudFacilityCount}
                        icon={
                            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-lightPrimary text-2xl text-brand-500 dark:!bg-navy-700 dark:text-white">
                                <FaRegBuilding className="text-[#f0b41c]" />
                            </div>
                        }
                    />
                )}
                <Statistics
                    title="Original Loan"
                    value={formatCurrency(originalLoan)}
                    icon={
                        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-lightPrimary text-2xl text-brand-500 dark:!bg-navy-700 dark:text-white">
                            <DollarIcon className="text-[#f0b41c]" />
                        </div>
                    }
                />
                {shouldShowFacilityStats && (
                    <Statistics
                        title="Total HUD Beds"
                        value={hudTotalBeds}
                        icon={
                            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-lightPrimary text-2xl text-brand-500 dark:!bg-navy-700 dark:text-white">
                                <BedIcon className="text-[#f0b41c]" />
                            </div>
                        }
                    />
                )}
                <Statistics
                    title="Current Loan Balance"
                    value={formatCurrency(currentLoanBalance)}
                    icon={
                        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-lightPrimary text-2xl text-brand-500 dark:!bg-navy-700 dark:text-white">
                            <DollarIcon className="text-[#f0b41c]" />
                        </div>
                    }
                />
                {!shouldShowFacilityStats && (
                    <Statistics
                        title="Endorsement Date"
                        value={formatDate(endorsementDate)}
                        icon={
                            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-lightPrimary text-2xl text-brand-500 dark:!bg-navy-700 dark:text-white">
                                <MdDateRange className="text-[#f0b41c]" />
                            </div>
                        }
                    />
                )}
                <Statistics
                    title="Interest Rate"
                    value={`${(interestRate * 100).toFixed(2)}%`}
                    icon={
                        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-lightPrimary text-2xl text-brand-500 dark:!bg-navy-700 dark:text-white">
                            <FaPercent className="text-[#f0b41c]" />
                        </div>
                    }
                />
                <Statistics
                    title="Debt Service"
                    value={formatCurrency(debtService)}
                    icon={
                        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-lightPrimary text-2xl text-brand-500 dark:!bg-navy-700 dark:text-white">
                            <DollarIcon className="text-[#f0b41c]" />
                        </div>
                    }
                />
                <Statistics
                    title="Term"
                    value={term}
                    icon={
                        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-lightPrimary text-2xl text-brand-500 dark:!bg-navy-700 dark:text-white">
                            <MdDateRange className="text-[#f0b41c]" />
                        </div>
                    }
                />
            </div>
        </Card>
    );
};

export default HudDebt;